import { isBase64ImageString } from '@src/utils/data-utils';
import { isObject, isString } from '@src/utils/type-utils';
import toWebpImage from '@src/utils/to-webp-image';

// noinspection SpellCheckingInspection
/**
 * 单个图片预加载
 * @param {string} src - 图片路径
 * @param {boolean} [decode] - 是否解码
 * @param {boolean} [shouldConvert2Webp] 在当前环境支持 webp 的情况下自动解析支持 webp
 * @returns {Promise<string>}
 */
export const preloadImage = async (src, { decode, shouldConvert2Webp = true } = {}) => {
  if (isBase64ImageString(src)) {
    return Promise.resolve(src);
  }
  if (shouldConvert2Webp) {
    src = toWebpImage(src);
  }
  const image = new Image();
  await new Promise(resolve => {
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
  });
  if (decode && image.decode) {
    // f=>f, 用于解决可能报错的场景
    await image.decode().then(f => f, f => f);
  }
  return src;
};

/**
 * 过滤预加载图片
 * @param {string[]} images - 过滤图片
 * @return {string[]}
 */
export function filterPreloadImages(images) {
  if (isObject(images)) {
    images = Object.keys(images).map(key => images[key]);
  }
  return images.filter(imagePath => {
    return !isBase64ImageString(imagePath) && isString(imagePath);
  });
}

// noinspection SpellCheckingInspection
/**
 * 多个图片预加载
 * @param {string[]} images - 图片路径列表
 * @param {boolean} [decode = true] - 是否解码, 默认: true
 * @param {boolean} [shouldConvert2Webp = true] 在当前环境支持 webp 的情况下自动解析支持 webp, 默认: true
 * @returns {Promise<string[]>}
 */
export default function preloadImages(images = [], { decode = true, shouldConvert2Webp = true } = {}) {
  const preloadPromises = filterPreloadImages(images).map(src => {
    if (shouldConvert2Webp) {
      src = toWebpImage(src);
    }
    return preloadImage(src, { decode });
  });
  return Promise.all(preloadPromises);
}
